import { useEffect, useRef, useState } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { fetchCollectionProducts, ShopifyProduct } from "@/lib/shopify";
import LocaleLink from "@/components/LocaleLink";
import { useLocale } from "@/lib/i18n";
import ThumbhashImage from "@/components/ThumbhashImage";
import GalleryMagnifier from "./GalleryMagnifier";


// Mount the first row eagerly; defer the rest until they're about to scroll into view.
const INITIAL_TILES = 6;


const DesignGallery = () => {
  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mountAll, setMountAll] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const { country } = useLocale();

  useEffect(() => {
    fetchCollectionProducts("featured-home", 24, country)
      .then((items) => {
        setProducts(items);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to fetch collection:", err);
        setError("Failed to load designs");
        setLoading(false);
      });
  }, [country]);

  useEffect(() => {
    if (mountAll || loading || products.length <= INITIAL_TILES) return;
    const el = sentinelRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setMountAll(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setMountAll(true);
          io.disconnect();
        }
      },
      { rootMargin: "800px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [mountAll, loading, products.length]);


  return (
    <section id="design-gallery" className="pt-6 md:pt-12 pb-12 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10 md:mb-20">
          <h2 className="font-light text-4xl md:text-4xl text-shaman-gold tracking-tight">
            <span className="text-6xl">24</span><br /> UNIQUE DESIGNS
          </h2>
        </div>
        {loading ? (
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 md:gap-2.5 -mx-2 md:-mx-3">
            {Array.from({ length: 24 }).map((_, i) => (
              <Skeleton key={i} className="w-full aspect-1/3 rounded-xl" />
            ))}
          </div>
        ) : error ? (
          <p className="text-center text-muted-foreground py-12">{error}</p>
        ) : products.length === 0 ? (
          <p className="text-center text-muted-foreground py-12">No products found in the "Home" collection.</p>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 md:gap-2.5 -mx-2 md:-mx-3">
            {(mountAll ? products : products.slice(0, INITIAL_TILES)).map((product, index) => {
              const image = product.node.images.edges[0]?.node;
              return (
                <LocaleLink
                  to={`/product/${product.node.handle}`}
                  key={product.node.id}
                  className="group relative overflow-hidden aspect-[0.37076674277] rounded-xl shadow-card hover:shadow-elevated transition-all duration-300 hover:scale-[1.02] cursor-pointer block bg-black"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  {image ? (
                      <ThumbhashImage
                        src={image.url}
                        layout="fullWidth"
                        aspectRatio={0.37076674277}
                        sizes="(min-width: 1280px) 200px, (min-width: 640px) 16vw, 30vw"
                        breakpoints={[200, 300, 400, 600, 800]} 
                        alt={image.altText || product.node.title}
                        className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105"
                        priority={index < 6}
                        decoding="async"
                      />
                  ) : (
                    <div className="w-full aspect-1/3 bg-muted flex items-center justify-center">
                      <span className="text-muted-foreground text-xs">No image</span>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-linear-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </LocaleLink>
              );
            })}
            {!mountAll && products.length > INITIAL_TILES && (
              <div ref={sentinelRef} className="col-span-full h-1" aria-hidden="true" />
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default DesignGallery;
